import { getSimplifiedDate } from 'helpers/dateHelper';
import { isFreeTrial } from 'helpers/userHelper';
import {
  PREMIUM_FEATURE_ADVANCED_MONITORING,
  PREMIUM_FEATURE_CREDIT_REPORT,
  PREMIUM_FEATURE_ID_THEFT_PROTECTION,
  PREMIUM_FEATURE_INSTANT_OFFERS,
  PREMIUM_FEATURE_LIVE_EXPERTS,
  PREMIUM_FEATURE_RENT_REPORTING,
} from 'types/premium.constants';
import { isEmpty } from 'helpers/string.helper';
import { isCashUser, isBuilderUser, isTrialEligible } from 'helpers/userHelper';

// Imported the Normalize variation 1 from the Optimizely dashboard
export const csTilesOrder = [
  'INSTANT_CREDIT_OFFERS',
  'SMART_CREDIT_REPORTS',
  'RENT_REPORTING',
  'CREDIT_CALENDAR',
  'CREDIT_BUILDER',
  'CREDIT_DISPUTE_ASSISTANCE',
  'CREDIT_AND_ID_PROTECTION_MONITORING',
  'ONE_M_IDENTITY_THEFT_INSURANCE',
];

const getSortedList = (tilesOrder, creditToolsArrayTilesList, userInfo) => {
  let sortedCreditTools = [];

  const convertedKeyValueCreditToolsArray = creditToolsArrayTilesList?.reduce(
    (creditTools, item) => {
      creditTools[item?.id] = item;
      return creditTools;
    },
    {},
  );

  if (!isEmpty(tilesOrder)) {
    sortedCreditTools = tilesOrder?.reduce((tool, id) => {
      if (convertedKeyValueCreditToolsArray?.[id]) {
        tool.push(convertedKeyValueCreditToolsArray?.[id]);
      }
      return tool;
    }, []);
  }

  return sortedCreditTools.length > 0
    ? sortedCreditTools
    : creditToolsArrayTilesList;
};

export const getCreditToolsArrayTiles = ({
  scoreSimulatorEnabled,
  isPlusUser,
  bureauScoreDetails,
  rentReportingMapped,
  isPremiumUser,
  userInfo,
  newCsidAlerts,
  newAlertsCreditMonitoring,
  tilesOrder = csTilesOrder,
  isCBuilderUser,
}) => {
  const creditToolsArrayTilesList = [
    {
      id: 'CREDIT_CALENDAR',
      title: 'Credit Calendar',
      notification: isPlusUser
        ? ''
        : 'Access your 30-day credit calendar with Sesame+ <br /><a>Learn more</a>',
      icon: 'creditToolIcon',
      isAlert: false,
      isSuccess: false,
      highlighted: false,
      disabled: !isPlusUser,
      plusUrl: '/creditManager/calendar',
      freeUrl: '/creditManager/calendar',
      reactivateUrl: '/creditManager/calendar',
    },
    {
      id: 'SMART_CREDIT_REPORTS',
      title: 'Smart Credit Reports',
      notification: `Last viewed report ${getSimplifiedDate(
        bureauScoreDetails?.creditProfileRefreshDate?.equifax,
      )}`,
      icon: 'actionsOutline',
      isAlert: false,
      isSuccess: false,
      highlighted: false,
      disabled: !isPlusUser,
      freeUrl: '/plus?feature=' + PREMIUM_FEATURE_CREDIT_REPORT,
      reactivateUrl:
        '/plus/reactivate?feature=' + PREMIUM_FEATURE_CREDIT_REPORT,
      plusUrl: '/creditReports/tabs',
    },
    {
      id: 'INSTANT_CREDIT_OFFERS',
      title: 'Instant Credit Offer',
      isTop: true,
      notification:
        '<div className="bgNeutralN500" style={{"borderRadius": "10px", "textAlign" : "center", width: "80px"}}><span className="fontWhite">sesame+</span><span className="fontYellow7">pick</span></div>',
      icon: 'accountMixCreditCard',
      isAlert: false,
      isSuccess: false,
      highlighted: false,
      disabled: !isPlusUser,
      plusUrl: '/offers',
      freeUrl: '/plus?feature=' + PREMIUM_FEATURE_INSTANT_OFFERS,
      reactivateUrl:
        '/plus/reactivate?feature=' + PREMIUM_FEATURE_INSTANT_OFFERS,
    },
    {
      id: 'CREDIT_AND_ID_PROTECTION_MONITORING',
      title: 'Credit & ID Protection Monitoring',
      notification: `Last updated ${getSimplifiedDate(
        bureauScoreDetails?.creditProfileRefreshDate?.equifax,
      )}`,
      icon: 'alertsOutline',
      isAlert: false,
      isSuccess: false,
      highlighted: false,
      disabled: !isPlusUser,
      plusUrl: '/alerts/creditMonitoring',
      freeUrl: '/plus?feature=' + PREMIUM_FEATURE_ADVANCED_MONITORING,
      reactivateUrl:
        '/plus/reactivate?feature=' + PREMIUM_FEATURE_ADVANCED_MONITORING,
    },
    {
      id: 'RENT_REPORTING',
      title: 'Rent Reporting',
      notification: rentReportingMapped?.message,
      secondLabel: 'Improve payment history',
      icon: 'creditReportCreditTools',
      isAlert: false,
      isSuccess: false,
      highlighted: false,
      disabled: !isPlusUser,
      plusUrl: '/rentPaymentReportingHome',
      fontColorOverRide: rentReportingMapped?.color,
      freeUrl: '/plus?feature=' + PREMIUM_FEATURE_RENT_REPORTING,
      reactivateUrl:
        '/plus/reactivate?feature=' + PREMIUM_FEATURE_RENT_REPORTING,
    },
    {
      id: 'CREDIT_BUILDER',
      title: 'Credit Builder',
      notification: '',
      icon: 'increaseScoreOutline',
      secondLabel: 'Improve payment history',
      isAlert: false,
      isSuccess: false,
      highlighted: false,
      disabled: !isPlusUser && !isCBuilderUser,
      plusUrl: '/cash',
      freeUrl: '/cash',
      reactivateUrl: '/cash',
    },
    {
      id: 'ONE_M_IDENTITY_THEFT_INSURANCE',
      title: '$1M Identity Theft Insurance',
      notification: 'Covered',
      icon: 'shieldFilled',
      isAlert: false,
      isSuccess: true,
      highlighted: false,
      disabled: !isPlusUser,
      plusUrl: '/liveExperts?tab=identityTheftInsurance',
      freeUrl: '/plus?feature=' + PREMIUM_FEATURE_ID_THEFT_PROTECTION,
      reactivateUrl:
        '/plus/reactivate?feature=' + PREMIUM_FEATURE_ID_THEFT_PROTECTION,
    },
    {
      id: 'CREDIT_DISPUTE_ASSISTANCE',
      title: 'Credit Dispute Assistance',
      notification: 'Contact Support Specialist',
      icon: 'creditDisputeOutline',
      isAlert: false,
      isSuccess: false,
      highlighted: false,
      disabled: !isPlusUser,
      plusUrl: '/liveExperts?tab=creditDisputeResolution',
      freeUrl: '/plus?feature=' + PREMIUM_FEATURE_LIVE_EXPERTS,
      reactivateUrl: '/plus/reactivate?feature=' + PREMIUM_FEATURE_LIVE_EXPERTS,
    },
  ];

  const creditToolsArrayEightTiles = getSortedList(
    tilesOrder,
    creditToolsArrayTilesList,
    userInfo,
  );

  if (scoreSimulatorEnabled) {
    creditToolsArrayEightTiles?.push({
      id: 'SCORE_SIMULATOR',
      title: 'Score Simulator',
      notification: isPlusUser
        ? ''
        : 'See how your actions can raise or lower your score.',
      isTop: true,
      icon: 'creditScoreTile',
      isAlert: false,
      isSuccess: false,
      highlighted: false,
      disabled: !isPlusUser,
      freeUrl: '/plus',
      reactivateUrl: '/plus',
      plusUrl: '/scoreSimulator',
    });
  }

  const creditToolsArraySixTiles = [
    {
      title: 'Credit Reports',
      notification:
        isPremiumUser && !isFreeTrial(userInfo)
          ? `Last viewed report ${getSimplifiedDate(
              bureauScoreDetails?.creditProfileRefreshDate?.equifax,
            )}`
          : 'Not available in free trial. Skip trial to get all reports now',
      icon: 'actionsOutline',
      isAlert: false,
      isSuccess: false,
      highlighted: false,
      disabled: !isPremiumUser,
      freeUrl:
        '/premium?feature=CREDITREPORT&opt_var=threedialsfreetrial_614_tiles',
      plusUrl: isFreeTrial(userInfo)
        ? '/creditReports/view?nav=premiumCreditReports&modal=TRIAL'
        : 'creditReports/tabs',
    },
    {
      title: 'ID Protection Alerts',
      notification: `${newCsidAlerts} new alerts`,
      icon: 'shieldFilled',
      isAlert: false,
      isSuccess: false,
      highlighted: false,
      disabled: !isPremiumUser,
      plusUrl: 'alerts/idProtection',
      freeUrl:
        '/premium?feature=WALLETPROTECTION&opt_var=threedialsfreetrial_614_tiles',
    },
    {
      title: 'Credit Monitoring',
      notification: isPremiumUser
        ? `${newAlertsCreditMonitoring} new alerts`
        : 'Real-time alerts about your credit profile, SSN activity... more',
      icon: 'alertsOutline',
      isAlert: false,
      isSuccess: false,
      highlighted: false,
      disabled: !isPremiumUser,
      plusUrl: 'alerts/creditMonitoring',
      freeUrl:
        '/premium?feature=MONITORING&opt_var=threedialsfreetrial_614_tiles',
    },
    {
      title: 'Rent Reporting',
      notification: isPremiumUser
        ? rentReportingMapped?.message
        : 'Paying rent with the power of a mortgage payment on your... more',
      secondLabel: 'Improve payment history',
      icon: 'creditReportCreditTools',
      isAlert: false,
      isSuccess: false,
      highlighted: false,
      disabled: !isPremiumUser,
      plusUrl: 'rentPaymentReportingHome',
      fontColorOverRide: rentReportingMapped?.color,
      freeUrl:
        '/premium?feature=RENTREPORTING&opt_var=threedialsfreetrial_614_tiles',
    },
    {
      title: '$1M Identity Theft Insurance',
      notification: 'Covered',
      icon: 'shieldFilled',
      isAlert: false,
      isSuccess: true,
      highlighted: false,
      disabled: !isPremiumUser,
      plusUrl: '/liveExperts?tab=identityTheftInsurance',
      freeUrl:
        '/premium?feature=IDTHEFTPROTECTION&opt_var=threedialsfreetrial_614_tiles',
    },
    {
      title: 'Credit Dispute Resolution',
      notification: 'Fix inaccurate information',
      icon: 'creditDisputeOutline',
      isAlert: false,
      isSuccess: false,
      highlighted: false,
      disabled: !isPremiumUser,
      plusUrl: '/liveExperts?tab=creditDisputeResolution',
      freeUrl:
        '/premium?feature=LIVEEXPERTS&opt_var=threedialsfreetrial_614_tiles',
    },
  ];

  return { creditToolsArrayEightTiles, creditToolsArraySixTiles };
};

export const getRedirectUrl = ({ tileInfo, userInfo, cashAccount }) => {
  const { plusUrl, disabled, freeUrl, reactivateUrl, title } = tileInfo ?? {};

  if (
    title === 'Credit Builder' &&
    isCashUser(userInfo) &&
    !isBuilderUser(cashAccount)
  ) {
    return '/cb20Enrollment';
  }

  const isTrialEligibleUser = isTrialEligible(userInfo);

  if (disabled) {
    return isTrialEligibleUser ? freeUrl : reactivateUrl;
  } else {
    return plusUrl;
  }
};
